import {constant} from 'lodash/fp';

import {PULL_TO_REFRESH_TIME_COUNT} from '/constant/time.constant';
import {
  METRIC_ALL,
  METRIC_ATTR_IS_LOADING,
  METRIC_ATTR_LAST_REFRESHED,
} from '/enum/metric.enum';
import {
  FETCH_CLIENTS_DASHBOARD,
  FETCH_HOME_DASHBOARD,
  FETCH_TEAMS_DASHBOARD,
} from '/enum/request.enum';
import {
  RESPONSE_SCHEMA_DASHBOARD_CLIENTS,
  RESPONSE_SCHEMA_DASHBOARD_HOME,
  RESPONSE_SCHEMA_DASHBOARD_TEAMS,
} from '/enum/responseSchema.enum';
import {
  getAcceleratorBonusBanner,
  getClientSupportBonusPlusBanner,
  getPerformance,
  getProfile,
  getRankProgression,
  getSameDayLastMonth,
} from '/redux/action/coach.action';
import {reset as resetReportData} from '/redux/action/report.action';
import {request} from '/redux/api/request';
import {userId as getUserId} from '/redux/selector/currentUser.selector';
import {createPromisedAction, handleMissingFields} from '/util/action.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import {delayFor} from '/util/timer.util';

import {getErrorHandler} from './apiError.action';

export const GET_CLIENT_METRICS: string = 'METRIC/GET_CLIENTS';
export const GET_TEAMS_METRICS: string = 'METRIC/GET_TEAMS';
export const GET_HOME_METRICS: string = 'METRIC/GET_HOME_DASHBOARD';
export const GET_REFRESHED_METRICS: string = 'METRIC/GET_REFRESHED_METRICS';
export const SET_METRIC_META: string = 'METRIC/SET_METRIC_META';

export const getClientMetrics = createPromisedAction(
  GET_CLIENT_METRICS,
  [],
  // @ts-expect-error TS(2345): Argument of type '(dispatchMe: any, inputPayload: ... Remove this comment to see the full error message
  (dispatchMe: any, inputPayload: any, dispatch: any, getState: any) => {
    return request(FETCH_CLIENTS_DASHBOARD)
      .then(handleMissingFields(RESPONSE_SCHEMA_DASHBOARD_CLIENTS))
      .then(({clients, orders, candidates}) => {
        return dispatchMe(() => ({
          userId: getUserId(getState()),
          clients,
          orders,
          candidates,
        }));
      })
      .catch(
        getErrorHandler(dispatch, {
          promisedAction: getClientMetrics,
          params: inputPayload,
        }),
      );
  },
);

export const getTeamsMetrics = createPromisedAction(
  GET_TEAMS_METRICS,
  [],
  // @ts-expect-error TS(2345): Argument of type '(dispatchMe: any, inputPayload: ... Remove this comment to see the full error message
  (dispatchMe: any, inputPayload: any, dispatch: any, getState: any) => {
    return request(FETCH_TEAMS_DASHBOARD)
      .then(handleMissingFields(RESPONSE_SCHEMA_DASHBOARD_TEAMS))
      .then(
        ({teams, coaches, rankPerformance, closeToRank, bonuses, atRisk}) => {
          return dispatchMe(() => ({
            atRisk,
            bonuses,
            closeToRank,
            coaches,
            rankPerformance,
            teams,
            userId: getUserId(getState()),
          }));
        },
      )
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        getErrorHandler(dispatch, {
          promisedAction: getTeamsMetrics,
          params: inputPayload,
        }),
      );
  },
);

export const getHomeMetrics = createPromisedAction(
  GET_HOME_METRICS,
  [],
  // @ts-expect-error TS(2345): Argument of type '(dispatchMe: any, inputPayload: ... Remove this comment to see the full error message
  (dispatchMe: any, inputPayload: any, dispatch: any, getState: any) => {
    return request(FETCH_HOME_DASHBOARD)
      .then(handleMissingFields(RESPONSE_SCHEMA_DASHBOARD_HOME))
      .then(
        ({
          frontlineQualifyingVolume,
          gen0Volume,
          totalGenerationVolume,
          clients,
          coaches,
          teams,
          allOrgClientsWithExpiringWellnessCreditsCount,
          allOrgClientsWithExpiringRewardsCount,
          frontlineClientsWithExpiringWellnessCreditsCount,
          frontlineClientsWithExpiringRewardsCount,
        }) => {
          const refreshDateUtil = getDateUtilDefaultInstance();
          const lastRefreshDate = refreshDateUtil.getNow();

          dispatch(
            setMeta({
              metricKey: METRIC_ALL,
              meta: {
                [METRIC_ATTR_LAST_REFRESHED]: lastRefreshDate,
              },
            }),
          );

          return dispatchMe(() => ({
            frontlineQualifyingVolume,
            gen0Volume,
            totalGenerationVolume,
            clients,
            coaches,
            teams,
            allOrgClientsWithExpiringWellnessCreditsCount,
            allOrgClientsWithExpiringRewardsCount,
            frontlineClientsWithExpiringWellnessCreditsCount,
            frontlineClientsWithExpiringRewardsCount,
            userId: getUserId(getState()),
          }));
        },
      )
      .catch(
        // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
        getErrorHandler(dispatch, {
          promisedAction: getHomeMetrics,
          params: inputPayload,
        }),
      );
  },
);

export const getRefreshedMetrics = createPromisedAction(
  GET_REFRESHED_METRICS,
  ['coachId', 'rankId'],
  // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.

  (dispatchMe: any, {coachId, rankId}: any, dispatch: any, _getState: any) => {
    // First reset report state to fetch data again
    // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
    dispatch(resetReportData());

    return delayFor(PULL_TO_REFRESH_TIME_COUNT)
      .then(() =>
        // Before fetching refreshed data, set the "isLoading" state to true.
        dispatch(
          setMeta({
            metricKey: METRIC_ALL,
            meta: {[METRIC_ATTR_IS_LOADING]: true},
          }),
        ),
      )
      .then(() =>
        Promise.all([
          dispatch(getProfile({coachId})),
          dispatch(getSameDayLastMonth({coachId})),
          // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
          dispatch(getHomeMetrics()),
          dispatch(getPerformance({coachId})),
          // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
          dispatch(getTeamsMetrics()),
          // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
          dispatch(getClientMetrics()),
          // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
          dispatch(getAcceleratorBonusBanner()),
          // @ts-expect-error TS(2554): Expected 1 arguments, but got 0.
          dispatch(getClientSupportBonusPlusBanner()),
          dispatch(getRankProgression({coachId, rankId})),
        ]),
      )
      .then(() => {
        const refreshDateUtil = getDateUtilDefaultInstance();
        const lastRefreshDate = refreshDateUtil.getNow();

        dispatch(
          setMeta({
            metricKey: METRIC_ALL,
            meta: {
              [METRIC_ATTR_IS_LOADING]: false,
              [METRIC_ATTR_LAST_REFRESHED]: lastRefreshDate,
            },
          }),
        );
      })
      .catch(constant(null));
  },
);

export const setMeta = createPromisedAction(SET_METRIC_META, [
  'metricKey',
  'meta',
]);
